import {
  useState,
  useEffect,
  useCallback,
  LegacyRef,
  PropsWithChildren,
  FC
} from 'react'
import useEmblaCarousel from 'embla-carousel-react'
import { EmblaCarouselType } from 'embla-carousel'
import { WheelGesturesPlugin } from 'embla-carousel-wheel-gestures'
import { Gallery, GalleryProps, Item } from 'react-photoswipe-gallery'
import { resizeShopifyImage } from '@helpers/shopify'
import { ReactComponent as Arrow } from '@svgs/chevron-slim.svg'
import styles from './productGrid.module.scss'
import classNames from 'classnames'
import { analytics, analyticsPostHogOmit } from '@helpers/analytics'
import photoswipeStyles from './photoswipe.module.scss'

const PhotoswipeGallery = Gallery as FC<PropsWithChildren<GalleryProps>>

const preventEdgeScrolling = (embla: EmblaCarouselType) => {
  const { limit, target, location, scrollTo } = embla.internalEngine()

  return () => {
    if (limit.reachedMax(target.get())) {
      if (limit.reachedMax(location.get())) location.set(limit.max)
      target.set(limit.max)
      scrollTo.distance(0, false)
    }
    if (limit.reachedMin(target.get())) {
      if (limit.reachedMin(location.get())) location.set(limit.min)
      target.set(limit.min)
      scrollTo.distance(0, false)
    }
  }
}

type ThumbProps = {
  selected: boolean
  imgSrc: string
  onClick: () => void
}

export function Thumb({ selected, imgSrc, onClick }: ThumbProps) {
  return (
    <div className={styles.Thumb__slide}>
      <button
        onClick={onClick}
        className={`${styles.Thumb__slideButton} ${
          selected ? styles.selected : ''
        }`}
        type="button"
      >
        <img
          className={styles.Thumb__slideImage}
          src={imgSrc}
          alt="Your alt text"
        />
      </button>
    </div>
  )
}

type Image = {
  alt: string
  src: string
}

type ProductGridProps = {
  productName: string
  images: Image[]
  setIsImageGalleryOpen: (arg: boolean) => void
}

function ProductGrid({
  images,
  productName,
  setIsImageGalleryOpen
}: ProductGridProps) {
  const [selectedIndex, setSelectedIndex] = useState(0)
  const [emblaMainRef, emblaMainApi] = useEmblaCarousel({ loop: true })
  const [emblaThumbsRef, emblaThumbsApi] = useEmblaCarousel(
    {
      containScroll: 'keepSnaps',
      dragFree: true,
      axis: 'y'
    },
    [
      WheelGesturesPlugin({
        forceWheelAxis: 'y'
      })
    ]
  )

  const productImagesStyles = classNames(
    styles.productImages__list,
    styles.ProductGrid,
    photoswipeStyles.photoswipe
  )

  const imagesLoaded = (imageIndex: number) => {
    analyticsPostHogOmit('Gallery interaction', {
      action: 'Gallery opened',
      label: `Image (${imageIndex})`
    })
    analytics('Click', {
      category: 'Product Page',
      label: `Image (${imageIndex})`
    })
  }

  const onThumbClick = useCallback(
    (index: number) => {
      if (!emblaMainApi || !emblaThumbsApi) return
      emblaMainApi.scrollTo(index)
    },
    [emblaMainApi, emblaThumbsApi]
  )

  const onSelect = useCallback(() => {
    if (!emblaMainApi || !emblaThumbsApi) return
    setSelectedIndex(emblaMainApi.selectedScrollSnap())
    emblaThumbsApi.scrollTo(emblaMainApi.selectedScrollSnap())
  }, [emblaMainApi, emblaThumbsApi, setSelectedIndex])

  const scrollPrev = useCallback(() => {
    emblaMainApi?.scrollPrev()
    emblaThumbsApi?.scrollPrev()
  }, [emblaMainApi, emblaThumbsApi])

  const scrollNext = useCallback(() => {
    emblaMainApi?.scrollNext()
    emblaThumbsApi?.scrollNext()
  }, [emblaMainApi, emblaThumbsApi])

  useEffect(() => {
    if (!emblaMainApi || !emblaThumbsApi) return
    onSelect()
    emblaMainApi.on('select', onSelect)
    emblaMainApi.on('reInit', onSelect)
    emblaThumbsApi.on('scroll', preventEdgeScrolling(emblaThumbsApi))
  }, [emblaMainApi, emblaThumbsApi, onSelect])

  useEffect(() => {
    if (!emblaMainApi || !emblaThumbsApi) return

    setSelectedIndex(0)
    emblaThumbsApi.scrollTo(0, true)
    emblaMainApi.scrollTo(0, true)
  }, [productName])

  return (
    <div id="product-image-gallery" className={productImagesStyles}>
      <PhotoswipeGallery
        options={{
          captionEl: false,
          showHideOpacity: true,
          maxSpreadZoom: 4,
          closeOnScroll: false,
          preload: [1, 3],
          history: false,
          barsSize: { top: 0, bottom: 0 },
          loadingIndicatorDelay: 500,
          preloaderEl: true
        }}
        onOpen={photoswipe => {
          imagesLoaded(photoswipe.getCurrentIndex())
          setIsImageGalleryOpen(true)

          photoswipe.listen('close', () => {
            setIsImageGalleryOpen(false)
            analyticsPostHogOmit('Gallery interaction', {
              category: 'Product Page',
              action: 'Gallery closed'
            })
          })

          photoswipe.listen('beforeChange', (direction: number) => {
            analyticsPostHogOmit('Gallery interaction', {
              action: 'Carousel swipe',
              name: 'Carousel',
              label: `Carousel ${
                direction === 1 ? 'next' : 'previous'
              } ${photoswipe.getCurrentIndex()}`,
              context: 'default'
            })
          })
        }}
      >
        <div className={styles.thumbsGallery} ref={emblaThumbsRef}>
          <div className={styles.thumbsContainer}>
            {images.map((image, index) => (
              <Thumb
                onClick={() => onThumbClick(index)}
                selected={index === selectedIndex}
                imgSrc={
                  image?.src
                    ? resizeShopifyImage(image?.src, { width: '216' })
                    : '/'
                }
                key={index}
              />
            ))}
          </div>
        </div>
        <div className={styles.galleryWrapper}>
          <p className={styles.galleryCounter}>
            {selectedIndex + 1} / {images.length}
          </p>
          <button
            className={`${styles.galleryArrowButton} ${styles.buttonPrevious}`}
            onClick={scrollPrev}
            aria-label="Previous"
          >
            <Arrow width={10} height={20} />
          </button>
          <button
            className={`${styles.galleryArrowButton} ${styles.buttonNext}`}
            onClick={scrollNext}
            aria-label="Next"
          >
            <Arrow width={10} height={20} />
          </button>
          <div className={styles.gallery} ref={emblaMainRef}>
            <div className={styles.galleryContainer}>
              {images.map((image, index) => (
                <Item
                  key={index}
                  original={image?.src}
                  width={1644}
                  height={1644}
                >
                  {({ ref, open }) => (
                    <button
                      className={styles.gallerySlide}
                      onClick={() => open()}
                    >
                      <img
                        className={styles.gallerySlideImage}
                        ref={ref as LegacyRef<HTMLImageElement>}
                        src={resizeShopifyImage(image?.src, {
                          width: '1644'
                        })}
                        width={822}
                        height={822}
                        alt={image?.alt || productName}
                      />
                    </button>
                  )}
                </Item>
              ))}
            </div>
          </div>
        </div>
      </PhotoswipeGallery>
    </div>
  )
}

export default ProductGrid
